import type { ApiError, SessionUser } from '@appcycle/contracts';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
let csrfToken: string | null = null;
let organizationContext = typeof window !== 'undefined' ? window.localStorage.getItem('appcycle.organizationId') : null;
let productContext = typeof window !== 'undefined' ? window.localStorage.getItem('appcycle.productId') : null;

export function setApiContext(organizationId: string, productId: string): void {
  organizationContext = organizationId;
  productContext = productId;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem('appcycle.organizationId', organizationId);
    window.localStorage.setItem('appcycle.productId', productId);
  }
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrfToken) headers.set('X-AppCycle-CSRF', csrfToken);
  if (organizationContext) headers.set('X-AppCycle-Organization', organizationContext);
  if (productContext) headers.set('X-AppCycle-Product', productContext);
  const response = await fetch(`${API_URL}${path}`, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as ApiError | null;
    throw new ApiClientError(payload?.error.message ?? `Request failed with ${response.status}`, response.status, payload?.error.code ?? 'request_failed', payload?.error.details);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export interface SessionResponse {
  authenticated: boolean;
  user: SessionUser | null;
  csrfToken: string | null;
}

export async function getSession(): Promise<SessionResponse> {
  const session = await api<SessionResponse>('/api/v1/session');
  csrfToken = session.csrfToken;
  return session;
}

export function login(returnTo = window.location.pathname): void {
  window.location.assign(`${API_URL}/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
}

export function openLogin(returnTo = `${window.location.pathname}${window.location.search}`): void {
  window.location.assign(`/login?returnTo=${encodeURIComponent(returnTo)}`);
}

export async function logout(): Promise<void> {
  await api('/api/v1/session/logout', { method: 'POST' });
  csrfToken = null;
  window.location.assign('/');
}

export function jsonBody(value: unknown): Pick<RequestInit, 'body' | 'headers'> {
  return { body: JSON.stringify(value), headers: { 'Content-Type': 'application/json' } };
}
